import type { LogEntry, LoggerSettings } from '../phase1/types';

const DB_NAME = 'linqs-tag-logger';
const DB_VERSION = 1;
const STORE_ENTRIES = 'entries';
const STORE_SETTINGS = 'settings';
const DEFAULT_MAX_ENTRIES = 500;

type EntryInput = Omit<LogEntry, 'id'>;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_ENTRIES)) {
        db.createObjectStore(STORE_ENTRIES, {
          keyPath: 'id',
          autoIncrement: true
        });
      }
      if (!db.objectStoreNames.contains(STORE_SETTINGS)) {
        db.createObjectStore(STORE_SETTINGS);
      }
    };

    request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB'));
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };
  });
}

async function getDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = openDatabase();
  }
  return dbPromise;
}

function waitForTransaction(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

export async function loadEntries(): Promise<LogEntry[]> {
  const db = await getDb();
  const tx = db.transaction(STORE_ENTRIES, 'readonly');
  const store = tx.objectStore(STORE_ENTRIES);
  const entries = await requestToPromise<LogEntry[]>(store.getAll() as IDBRequest<LogEntry[]>);
  await waitForTransaction(tx);
  return entries ?? [];
}

export async function addEntry(entry: EntryInput): Promise<LogEntry> {
  const db = await getDb();
  const tx = db.transaction(STORE_ENTRIES, 'readwrite');
  const store = tx.objectStore(STORE_ENTRIES);
  const id = await requestToPromise<IDBValidKey>(store.add(entry));
  await waitForTransaction(tx);
  return { id: Number(id), ...entry };
}

export async function deleteEntries(ids: number[]): Promise<void> {
  if (ids.length === 0) {
    return;
  }
  const db = await getDb();
  const tx = db.transaction(STORE_ENTRIES, 'readwrite');
  const store = tx.objectStore(STORE_ENTRIES);
  for (const id of ids) {
    store.delete(id);
  }
  await waitForTransaction(tx);
}

export async function clearEntries(): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE_ENTRIES, 'readwrite');
  tx.objectStore(STORE_ENTRIES).clear();
  await waitForTransaction(tx);
}

export async function trimToLimit(limit: number): Promise<void> {
  if (limit <= 0) {
    await clearEntries();
    return;
  }
  const db = await getDb();
  const tx = db.transaction(STORE_ENTRIES, 'readwrite');
  const store = tx.objectStore(STORE_ENTRIES);
  const count = await requestToPromise<number>(store.count());
  const deleteCount = count - limit;
  if (deleteCount > 0) {
    let removed = 0;
    const cursorRequest = store.openCursor();
    await new Promise<void>((resolve, reject) => {
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor || removed >= deleteCount) {
          resolve();
          return;
        }
        cursor.delete();
        removed += 1;
        cursor.continue();
      };
      cursorRequest.onerror = () => reject(cursorRequest.error ?? new Error('Failed to trim entries'));
    });
  }
  await waitForTransaction(tx);
}

export async function loadSettings(): Promise<LoggerSettings> {
  const db = await getDb();
  const tx = db.transaction(STORE_SETTINGS, 'readonly');
  const store = tx.objectStore(STORE_SETTINGS);
  const stored = await requestToPromise<LoggerSettings | undefined>(
    store.get('settings') as IDBRequest<LoggerSettings | undefined>
  );
  await waitForTransaction(tx);
  if (stored) {
    return stored;
  }
  const defaults: LoggerSettings = { maxEntries: DEFAULT_MAX_ENTRIES };
  await saveSettings(defaults);
  return defaults;
}

export async function saveSettings(settings: LoggerSettings): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE_SETTINGS, 'readwrite');
  tx.objectStore(STORE_SETTINGS).put(settings, 'settings');
  await waitForTransaction(tx);
}
